import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

type UrlTransform = (url: string) => string | null | undefined;

/** Components whose children must NOT be tokenized (e.g. code fences whose
 *  text is later JSON-parsed by app-supplied renderers like ChatChartRenderer). */
const RAW_CHILDREN_KEYS = new Set<string>(['code', 'pre']);

/** Block-level components whose children are tokenized by their own wrapper. */
const BLOCK_KEYS = new Set<string>([
  'p',
  'li',
  'ul',
  'ol',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'table',
  'thead',
  'tbody',
  'tr',
  'pre',
  'hr',
]);

export interface FadeBlurTextProps {
  text: string;
  /** Render `text` as markdown. */
  markdown?: boolean;
  /** Override / extend react-markdown components (e.g. custom <a> for in-app navigation). */
  markdownComponents?: Components;
  /** Custom URL transform - defaults to react-markdown's safe scheme allowlist. Pass through to allow custom protocols like `revont://`. */
  urlTransform?: UrlTransform;
  /** While true, progressively reveals newly received content. */
  isStreaming?: boolean;
  /** Drop the trailing partial token while streaming. */
  trimTrailingPartial?: boolean;
  className?: string;
}

interface RevealState {
  revealedCount: number;
  animate: boolean;
}

const RevealContext = createContext<RevealState>({
  revealedCount: 0,
  animate: false,
});

const TOKEN_RE = /(\S+|\s+)/g;
const REVEAL_INTERVAL_MS = 25;

function tokenize(text: string): string[] {
  return text.match(TOKEN_RE) ?? [];
}

/** Reveals one stable word/token at a time without changing whitespace layout. */
function FadeToken({ index, children }: { index: number; children: ReactNode }) {
  const { revealedCount, animate } = useContext(RevealContext);
  if (!animate) return <>{children}</>;
  if (index >= revealedCount) return null;
  if (typeof children === 'string' && /^\s+$/.test(children)) {
    return <>{children}</>;
  }
  return (
    <span className="fbt-token" style={{ whiteSpace: 'pre' }}>
      {children}
    </span>
  );
}

function ConditionalBlock({
  startIdx,
  endIdx,
  children,
}: {
  startIdx: number;
  endIdx: number;
  children: ReactNode;
}) {
  const { revealedCount, animate } = useContext(RevealContext);
  if (animate && endIdx > startIdx && startIdx >= revealedCount) return null;
  return <>{children}</>;
}

/** Renders Markdown text as ordered tokens. Whitespace stays plain, while
 *  newly revealed non-whitespace tokens receive the CSS blur-fade. Stable
 *  indices keep appended content from remounting earlier tokens. */
const DEFAULT_MARKDOWN_COMPONENTS: Components = {
  h1: ({ children }) => (
    <h1 className="!font-sans text-lg font-bold text-white mt-4 mb-2 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="!font-sans text-base font-semibold text-white mt-4 mb-2 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="!font-sans text-sm font-semibold text-white mt-3 mb-1.5 first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="!font-sans text-sm font-medium text-white mt-3 mb-1 first:mt-0">{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className="!font-sans text-xs font-medium text-white mt-2 mb-1 first:mt-0">{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className="!font-sans text-xs font-medium text-white/90 mt-2 mb-1 first:mt-0">{children}</h6>
  ),
  p: ({ children }) => (
    <p className="text-sm text-white/90 leading-relaxed my-2 first:mt-0 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside pl-5 space-y-1 my-2 text-white/90 text-sm">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside pl-5 space-y-1 my-2 text-white/90 text-sm">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="pl-0.5 leading-relaxed [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">
      {children}
    </li>
  ),
  code: ({ className, children }) => {
    const isCodeBlock = className?.includes('language-');
    if (isCodeBlock) {
      return (
        <code className="text-[13px] font-mono text-amber-200/90 leading-relaxed">{children}</code>
      );
    }
    return (
      <code className="px-1.5 py-0.5 rounded bg-white/10 text-amber-300/90 font-mono text-[13px]">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-3 p-3 rounded-lg bg-[rgba(0,0,0,0.3)] border border-white/10 overflow-x-auto">
      {children}
    </pre>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
  em: ({ children }) => <em className="italic text-white/80">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-white/20 pl-3 my-2 text-white/70 italic">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="my-3 max-w-full overflow-x-auto rounded-lg border border-white/10">
      <table className="min-w-full border-collapse text-left text-xs text-white/85">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-white/[0.06]">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-white/10">{children}</tbody>,
  tr: ({ children }) => <tr className="divide-x divide-white/10">{children}</tr>,
  th: ({ children }) => <th className="px-3 py-2 font-semibold text-white">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
  hr: () => <hr className="my-4 border-white/10" />,
};

interface SegmentCounter {
  i: number;
  blockTypes: Set<unknown>;
  rawTypes: Set<unknown>;
}

interface TokenizedMarkdownProps {
  children?: ReactNode;
  __fbtTokenized?: boolean;
  __fbtStartIdx?: number;
  __fbtEndIdx?: number;
}

/**
 * Counts tokens in a block before it is wrapped. This lets parent blocks such
 * as lists stay hidden until their first token is revealed.
 */
function wrapTokens(children: ReactNode, counter: SegmentCounter): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child === 'string') {
      return tokenize(child).map((token) => {
        const index = counter.i++;
        return <FadeToken key={index} index={index}>{token}</FadeToken>;
      });
    }
    if (isValidElement(child)) {
      if (counter.rawTypes.has(child.type)) return child;
      const isBlock = counter.blockTypes.has(child.type);
      const startIdx = counter.i;
      const inner = (child.props as TokenizedMarkdownProps).children;
      const wrappedChildren = wrapTokens(inner, counter);
      const endIdx = counter.i;
      return cloneElement(
        child as React.ReactElement<TokenizedMarkdownProps>,
        isBlock
          ? {
              __fbtTokenized: true,
              __fbtStartIdx: startIdx,
              __fbtEndIdx: endIdx,
            }
          : { __fbtTokenized: true },
        wrappedChildren,
      );
    }
    return child;
  });
}

interface MarkdownTreeProps {
  text: string;
  components: Components;
  counter: SegmentCounter;
  urlTransform: UrlTransform;
}

/** Memoized markdown tree that parses exactly the content received so far. */
const MarkdownTree = memo(function MarkdownTree({
  text,
  components,
  counter,
  urlTransform,
}: MarkdownTreeProps) {
  counter.i = 0;
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>
      {text}
    </ReactMarkdown>
  );
});

export function FadeBlurText({
  text,
  markdown = false,
  markdownComponents,
  urlTransform = defaultUrlTransform,
  isStreaming = false,
  trimTrailingPartial = false,
  className,
}: FadeBlurTextProps) {
  const { components, counter } = useMemo<{
    components: Components;
    counter: SegmentCounter;
  }>(() => {
    const merged: Components = {
      ...DEFAULT_MARKDOWN_COMPONENTS,
      ...(markdownComponents ?? {}),
    };
    const wrapped: Components = {};
    const counter: SegmentCounter = {
      i: 0,
      blockTypes: new Set<unknown>(),
      rawTypes: new Set<unknown>(),
    };
    (Object.keys(merged) as Array<keyof Components>).forEach((key) => {
      const Comp = merged[key];
      if (!Comp) return;
      const k = key as string;
      const passRawChildren = RAW_CHILDREN_KEYS.has(k);
      const isBlock = BLOCK_KEYS.has(k);
      const Wrapped = function WrappedMarkdownComponent(props: Record<string, unknown>) {
        const {
          children,
          __fbtTokenized,
          __fbtStartIdx,
          __fbtEndIdx,
          ...rest
        } = props as TokenizedMarkdownProps & Record<string, unknown>;
        // Inline components: parent block already tokenized our children.
        // Re-running wrapTokens here would double-count and push the range
        // far past their source-order position.
        if (!isBlock) {
          return (Comp as (p: Record<string, unknown>) => ReactNode)({ ...rest, children });
        }
        const isPreTokenized = __fbtTokenized === true;
        const startIdx = isPreTokenized ? (__fbtStartIdx ?? 0) : counter.i;
        const wrappedChildren =
          isPreTokenized || passRawChildren ? children : wrapTokens(children, counter);
        const endIdx = isPreTokenized ? (__fbtEndIdx ?? startIdx) : counter.i;
        const rendered = (Comp as (p: Record<string, unknown>) => ReactNode)({
          ...rest,
          children: wrappedChildren,
        });
        return (
          <ConditionalBlock startIdx={startIdx} endIdx={endIdx}>
            {rendered}
          </ConditionalBlock>
        );
      };
      if (isBlock) counter.blockTypes.add(Wrapped);
      if (passRawChildren) counter.rawTypes.add(Wrapped);
      (wrapped as Record<string, unknown>)[k] = Wrapped;
    });
    return { components: wrapped, counter };
  }, [markdownComponents]);

  const tokens = tokenize(text);
  const stable =
    trimTrailingPartial && isStreaming && tokens.length > 0 && /\S$/.test(text)
      ? tokens.slice(0, -1)
      : tokens;
  const stableText = stable.join('');
  const [revealedCount, setRevealedCount] = useState(0);
  const targetRef = useRef(stable.length);
  targetRef.current = stable.length;
  const animate = isStreaming;

  useEffect(() => {
    if (!animate) return;
    const timer = window.setInterval(() => {
      setRevealedCount((current) =>
        current < targetRef.current ? current + 1 : current,
      );
    }, REVEAL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [animate]);

  useEffect(() => {
    if (revealedCount > stable.length) setRevealedCount(stable.length);
  }, [revealedCount, stable.length]);

  const revealState = useMemo<RevealState>(
    () => ({ revealedCount, animate }),
    [animate, revealedCount],
  );

  if (markdown) {
    return (
      <RevealContext.Provider value={revealState}>
        <div className={className}>
          <MarkdownTree
            text={stableText}
            components={components}
            counter={counter}
            urlTransform={urlTransform}
          />
        </div>
      </RevealContext.Provider>
    );
  }

  if (!animate) {
    return (
      <span className={className} style={{ whiteSpace: 'pre-wrap' }}>
        {stableText}
      </span>
    );
  }

  return (
    <RevealContext.Provider value={revealState}>
      <span className={className} style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
        {stable.map((token, index) => (
          <FadeToken key={index} index={index}>{token}</FadeToken>
        ))}
      </span>
    </RevealContext.Provider>
  );
}
